/*
 * Model for tasks.
 */

define([
  "backbone",
  "jquery",
  "moment",
  "underscore",
  "messaging/messenger",
  "configuration/properties",
], function (Backbone, $, moment, _, Messenger, Properties) {
  "use strict";

  const TaskModel = {};

  const CHANGE_TYPE = [
    "Task Step Assigned",
    "Task Step Completed",
    "Task Created",
    "Task Completed",
    "Task Due Date Reached",
    "Task Reassigned",
  ];

  const DEFAULT_INTERVAL_AMOUNT = 1;

  const INTERVAL = {
    NONE: 0,
    YEARLY: 1,
    MONTHLY: 2,
    WEEKLY: 3,
    DAILY: 4,
    QUARTERLY: 5,
  };

  const PRIORITY = {
    LOW: 0,
    MEDIUM: 1,
    HIGH: 2,
    CRITICAL: 3,
  };

  const PRIORITY_DAYS = {
    LOW: 7,
    MEDIUM: 2,
    HIGH: 1,
    CRITICAL: 0,
  };

  const STATUS = {
    NOT_STARTED: 0,
    COMPLETED: 1,
    FAILED: 2,
    IN_PROGRESS: 3,
    IGNORED: 4,
    RESUBMITTED: 5,
    CREATED: 6,
    RECENTLY_COMPLETED: 7,
    ALL: -1,
  };

  const TYPE = {
    TOPIC_REVIEW: 0,
    UPDATE_RESPONSE: 1,
    TRANSLATE_RESPONSE: 2,
    ADVANCED: 3,
    TASK_AND_STEP: 4,
  };

  const TOPIC_EDIT_STATUS = {
    SUBMITTED: "SUBMITTED",
    ACCEPTED: "ACCEPTED",
    REJECTED: "REJECTED",
    STARTED: "STARTED",
    COMPLETE: "COMPLETE",
  };

  const ATTRIBUTE = {
    TASK_NAME: "name",
    TASK_DESCRIPTION: "description",
    TASK_START_DATE: "startDate",
    TASK_DUE_DATE: "dueDate",
  };

  TaskModel.DEFAULT_INTERVAL_AMOUNT = DEFAULT_INTERVAL_AMOUNT;
  TaskModel.INTERVAL = INTERVAL;
  TaskModel.PRIORITY = PRIORITY;
  TaskModel.PRIORITY_DAYS = PRIORITY_DAYS;
  TaskModel.STATUS = STATUS;
  TaskModel.TYPE = TYPE;
  TaskModel.TOPIC_EDIT_STATUS = TOPIC_EDIT_STATUS;

  TaskModel.task = Backbone.Model.extend({
    idAttribute: "id",
    defaults: {
      dueDate: null,
      description: "",
      comments: "",
      userId: 0,
      user: "",
      assignedUsers: "",
      status: STATUS.NOT_STARTED,
      type: TYPE.TOPIC_REVIEW,
      creatorId: 0,
      creator: "",
      expirationDate: null,
      hasExpiration: false,
      interval: TaskModel.INTERVAL.NONE,
      intervalAmount: DEFAULT_INTERVAL_AMOUNT,
      intentId: 0,
      intentName: "",
      name: "",
      notifyEmails: "",
      priority: TaskModel.PRIORITY.LOW,
      scheduled: false,
      startDate: null,
      createDate: null,
      finishDate: null,
      template: false,
      templateDescription: "",
      templateName: "",
      languageIds: [],
      touchpointIds: [],
    },
    initializeCustom: function () {
      this.on("change:user", function () {
        if (this.get("type") != TYPE.TOPIC_REVIEW) {
          // Review tasks do not have a touchpoint to validate
          this.userValidate();
        }
      });
      this.on("change:touchpointId", function () {
        this.userValidate(); // Don't need to check for task type because review tasks do not have a touchpoint to be
        // changed
      });
    },
    parse: function (resp) {
      if (resp) {
        if ("steps" in resp) {
          const steps = [];
          _.each(
            resp.steps,
            (step) => {
              const model = new TaskModel.taskStep(step, { parse: true });
              model.task = this;
              steps.push(model);
            },
            this,
          );
          this.steps().reset(steps);
          delete resp.steps;
        }
        if ("assignees" in resp) {
          const assignees = [];
          _.each(
            resp.assignees,
            (assignee) => {
              const model = new TaskModel.taskStepAssignee(assignee, { parse: true });
              model.task = this;
              model.taskStep = this;
              assignees.push(model);
            },
            this,
          );
          this.assignees().reset(assignees);
          delete resp.assignees;
        }
        if ("uploads" in resp) {
          const uploads = [];
          _.each(
            resp.uploads,
            (upload) => {
              const model = new TaskModel.taskUpload(upload, { parse: true });
              model.task = this;
              uploads.push(model);
            },
            this,
          );
          this.uploads().reset(uploads);
          delete resp.uploads;
        }
      }
      return resp;
    },
    assignees: function () {
      if (!this.assigneesCol) {
        this.assigneesCol = this.getSubModel(
          this.assigneesCol,
          TaskModel.taskStepAssignees,
          null,
          false,
          true,
        );
        this.assigneesCol.task = this;
        this.assigneesCol.taskStep = this;
        this.assigneesCol.on("add", () => (this.dirty = true));
        this.assigneesCol.on("remove", () => (this.dirty = true));
      }
      return this.assigneesCol;
    },
    steps: function () {
      if (!this.stepsCol) {
        this.stepsCol = this.getSubModel(this.stepsCol, TaskModel.taskSteps, null, false, true);
        this.stepsCol.task = this;
        this.stepsCol.on("add", () => (this.dirty = true));
        this.stepsCol.on("remove", () => (this.dirty = true));
      }
      return this.stepsCol;
    },
    uploads: function () {
      if (!this.uploadsCol) {
        this.uploadsCol = this.getSubModel(
          this.uploadsCol,
          TaskModel.taskUploads,
          null,
          false,
          true,
        );
        this.uploadsCol.task = this;
        this.uploadsCol.on("add", () => (this.dirty = true));
        this.uploadsCol.on("remove", () => (this.dirty = true));
      }
      return this.uploadsCol;
    },
    toJSON: function (options) {
      const json = _.clone(this.attributes);
      if (options && (options.parse || options.saving)) {
        if (this.stepsCol) {
          json.steps = [];
          this.stepsCol.each((step) => json.steps.push(step.toJSON(options)));
        }
        if (this.assigneesCol) {
          json.assignees = [];
          this.assigneesCol.each((assignee) => json.assignees.push(assignee.toJSON(options)));
        }
        if (this.uploadsCol) {
          json.uploads = [];
          this.uploadsCol.each((upload) => json.uploads.push(upload.toJSON(options)));
        }
      }
      return json;
    },
    userValidate: function () {
      const userId = this.get("userId");
      if (userId) {
        this.touchpointMap = Messenger.reqres.request("user:touchpoints", userId);
      } else {
        let userLoginId = this.get("user");
        if (userLoginId.length > 0) {
          userLoginId = userLoginId.substring(userLoginId.lastIndexOf("(") + 1);
          userLoginId = userLoginId.substring(0, userLoginId.lastIndexOf(")"));
          this.touchpointMap = Messenger.reqres.request("user:touchpoints", userLoginId);
        } else {
          return;
        }
      }
      this.touchpointMap.onSynced(function () {
        const kbId = Messenger.reqres.request("active:kb");
        const modelTouchpoint = +this.get("touchpointId");
        const touchpointList = this.touchpointMap.where({ kbId: kbId });
        let touchpointPermission = false;
        for (let i = 0; i < touchpointList.length; i++) {
          if (touchpointList[i].id === modelTouchpoint && touchpointList[i].get("hasPermissions")) {
            touchpointPermission = true;
          }
        }
        this.trigger("validate:touchpoint:permission", touchpointPermission);
      }, this);
    },
    url: function () {
      const kbId = Messenger.reqres.request("active:kb");
      const taskId = this.get("taskId");
      if (taskId) {
        return Properties.rootURL + "/" + kbId + "/tasks/" + taskId;
      } else {
        return Properties.rootURL + "/" + kbId + "/tasks";
      }
    },
    reset: function () {
      this.set(this.defaults); //set default attributes
      this.dirty = false; //don't need to save anymore
    },
    addComment: function (comment) {
      if (!comment || !comment.trim()) {
        return;
      }
      const existingComments = this.get("comments") || "";
      const userName = Messenger.reqres.request("username");
      const now = Messenger.reqres.request("get:formatted:dateTime", "now");
      // NOTE: jQuery converts \r, \n and \r\n to \r\n, so we just follow suit
      comment = "[" + userName + " " + now + "] " + comment.replace(/\r?\n/g, "\r\n");
      if (existingComments.trim()) {
        comment = existingComments + "\r\n" + comment;
      }
      this.set("comments", comment);
    },
    validateAttribute: function (name, value) {
      const today = moment.utc().startOf("day").toDate();
      switch (name) {
        case ATTRIBUTE.TASK_START_DATE:
          if (this.isNew()) {
            const startDate = Messenger.reqres.request(
              "to:dateUtc",
              this.get(ATTRIBUTE.TASK_START_DATE),
            );
            // adding Task via Topic tab does not set startDate
            if (startDate && startDate < today) {
              return $.t("task.startDateInPastError");
            }
          }
          break;
        case ATTRIBUTE.TASK_DUE_DATE:
          const dueDate = Messenger.reqres.request("to:dateUtc", this.get(ATTRIBUTE.TASK_DUE_DATE));
          const previousDueDate = Messenger.reqres.request(
            "to:dateUtc",
            this.previous(ATTRIBUTE.TASK_DUE_DATE),
          );
          const startDate = Messenger.reqres.request(
            "to:dateUtc",
            this.get(ATTRIBUTE.TASK_START_DATE),
          );
          const isDueDateChangeDetected =
            previousDueDate && dueDate.getTime() !== previousDueDate.getTime();
          if (this.isNew() || isDueDateChangeDetected) {
            if (dueDate < today) {
              return $.t("task.dueDateInPastError");
            }
            if (startDate && dueDate < startDate) {
              return $.t("task.dueDateBeforeStartDateError");
            }
          }

          break;
      }
    },
    _allowSave: function () {
      if (this.constructor.__super__._allowSave.apply(this, arguments)) {
        return true;
      }
      if (this.assigneesCol) {
        let dirty = false;
        this.assigneesCol.each((assignee) => {
          if (assignee.dirty) {
            dirty = true;
          }
        });
        if (dirty) {
          return true;
        }
      }
      if (this.stepsCol) {
        let dirty = false;
        this.stepsCol.each((step) => {
          if (step.dirty) {
            dirty = true;
          }
        });
        if (dirty) {
          return true;
        }
      }
      if (this.uploadsCol) {
        let dirty = false;
        this.uploadsCol.each((upload) => {
          if (upload.dirty) {
            dirty = true;
          }
        });
        if (dirty) {
          return true;
        }
      }
    },
  });

  // noinspection JSUnusedGlobalSymbols
  TaskModel.tasks = Backbone.Collection.extend({
    model: TaskModel.task,
    //Create new task and add it to the collection
    addTask: function (values, nonModelAttributes) {
      const task = new TaskModel.task();
      if (values) {
        task.set(values);
      }
      if (nonModelAttributes) {
        if (nonModelAttributes.languages) {
          task.languages = nonModelAttributes.languages;
        }
        if (nonModelAttributes.touchpoints) {
          task.touchpoints = nonModelAttributes.touchpoints;
        }
      }
      this.add(task);
      return task;
    },
    getResponseTasks: function () {
      return new TaskModel.tasks(this.where({ type: TYPE.UPDATE_RESPONSE }));
    },
    getTranslationTasks: function () {
      return new TaskModel.tasks(this.where({ type: TYPE.TRANSLATE_RESPONSE }));
    },
    getDisplayTasksCount: function () {
      //these three task statuses are the only ones we want to count because action can be taken on them
      const notStarted = this.where({ status: STATUS.NOT_STARTED }).length;
      const rejected = this.where({ status: STATUS.FAILED }).length;
      const inProgress = this.where({ status: STATUS.IN_PROGRESS }).length;
      return notStarted + rejected + inProgress;
    },
  });

  TaskModel.taskStep = Backbone.Model.extend({
    idAttribute: "stepId",
    defaults: function () {
      const data = {
        denialNextStep: 0,
        instructions: "",
        languageId: 1,
        order: 0,
        status: STATUS.CREATED,
      };
      if (this.task) {
        data.taskId = this.task.get("taskId");
      } else if (this.collection && this.collection.task) {
        data.taskId = this.collection.task.get("taskId");
      }
      return data;
    },
    url: function () {
      const kbId = Messenger.reqres.request("active:kb");
      let taskId = this.get("taskId");
      if (!taskId && this.task) {
        taskId = this.task.get("taskId");
        if (!taskId) {
          return "";
        }
      }
      if (this.id) {
        return Properties.rootURL + "/" + kbId + "/tasks/" + taskId + "/steps/" + this.id;
      } else {
        return Properties.rootURL + "/" + kbId + "/tasks" + taskId + "/steps";
      }
    },
    assignees: function () {
      this.assigneesCol = this.getSubModel(
        this.assigneesCol,
        TaskModel.taskStepAssignees,
        null,
        false,
        true,
      );
      this.assigneesCol.taskStep = this;
      return this.assigneesCol;
    },
    parse: function (resp) {
      if (resp) {
        if ("assignees" in resp) {
          const assignees = [];
          _.each(
            resp.assignees,
            function (assignee) {
              const model = new TaskModel.taskStepAssignee(assignee, { parse: true });
              model.taskStep = this;
              assignees.push(model);
            },
            this,
          );
          this.assignees().reset(assignees);
          delete resp.assignees;
        }
      }
      return resp;
    },
    toJSON: function (options) {
      const json = _.clone(this.attributes);
      if (options && (options.parse || options.saving)) {
        if (this.assigneesCol) {
          json.assignees = [];
          this.assigneesCol.each(function (assignee) {
            json.assignees.push(assignee.toJSON(options));
          });
        }
      }
      return json;
    },
  });

  TaskModel.taskSteps = Backbone.Collection.extend({
    model: TaskModel.taskStep,
    addStep: function (languageId) {
      const languageStepCount = this.reduce(function (memo, model) {
        if (model.get("languageId") == languageId) {
          return memo + 1;
        }
        return memo;
      }, 0);
      const taskStepData = {
        order: languageStepCount + 1,
        languageId: languageId,
      };
      const taskStep = new TaskModel.taskStep(taskStepData);
      taskStep.task = this.task;
      this.add(taskStep);
      return taskStep;
    },
    /**
     * Clone the steps for the first language into other available languages, but only if there
     * are no existing steps for that language.
     *
     * @param languageIds All of the language IDs for the task.
     */
    cloneLanguageStepsToEmptyLocales: function (languageIds) {
      if (!languageIds || languageIds.length < 2) {
        return;
      }
      const primaryLanguageId = languageIds[0];
      const languageSteps = this.filter(function (taskStep) {
        return taskStep.get("languageId") == primaryLanguageId;
      });
      if (!languageSteps || !languageSteps.length) {
        return;
      }
      for (let i = 1; i < languageIds.length; ++i) {
        const languageId = languageIds[i];
        const taskStep = this.findWhere({
          languageId: languageId,
        });
        if (!taskStep) {
          for (let j = 0; j < languageSteps.length; ++j) {
            const clonedStepData = languageSteps[j].toJSON({ saving: true });
            // noinspection JSUnresolvedVariable
            delete clonedStepData.stepId;
            clonedStepData.languageId = languageIds[i];
            _.each(clonedStepData.assignees, function (assignee) {
              delete assignee.id;
            });
            this.add(clonedStepData, { parse: true });
          }
        }
      }
    },
  });

  TaskModel.taskStepAssignee = Backbone.Model.extend({
    defaults: function () {
      const data = {
        departmentId: 0,
        userId: 0,
        allUsers: true,
        canEdit: true,
      };
      if (this.taskStep) {
        data.taskStepId = this.taskStep.id;
      } else if (this.collection && this.collection.taskStep) {
        data.taskStepId = this.collection.taskStep.id;
      }
      return data;
    },
  });

  TaskModel.taskStepAssignees = Backbone.Collection.extend({
    model: TaskModel.taskStepAssignee,
    addAssignee: function () {
      const assignee = new TaskModel.taskStepAssignee({
        taskStepId: this.taskStep ? this.taskStep.id : 0,
      });
      assignee.taskStep = this.taskStep;
      this.add(assignee);
      return assignee;
    },
  });

  TaskModel.taskStepAndAssignee = Backbone.Model.extend(
    {
      validateAttribute: function (name, value, allAttributes) {
        let allUsers;
        switch (name) {
          case "departmentId":
            if (value <= 0) {
              return $.t("task.step.departmentRequired");
            }
            break;
          case "instructions":
            return this.validateRequiredValue(value, {
              emptyValueMessage: "task.step.instructionsRequired",
            });
          case "userId":
            allUsers = allAttributes ? allAttributes.allUsers : this.get("allUsers");
            if (value <= 0 && !allUsers) {
              return $.t("task.step.departmentUserRequired");
            }
            break;
        }
      },
    },
    {
      createFromStepAndAssignee: function (
        taskStep,
        taskStepAssignee,
        taskStepAssignees,
        assigneeIndex,
        languageId,
        departments,
      ) {
        const assigneeData = _.extend(
          { readOnly: false },
          taskStep.attributes,
          taskStepAssignee.attributes,
        );
        switch (taskStep.get("status")) {
          case TaskModel.STATUS.FAILED:
          case TaskModel.STATUS.COMPLETED:
            assigneeData.readOnly = true;
        }
        assigneeData.firstAssignee = assigneeIndex === 0;
        assigneeData.lastAssignee = assigneeIndex === taskStepAssignees.length - 1;
        const departmentId = taskStepAssignee.get("departmentId");
        assigneeData.departmentUsers = null;
        if (departmentId) {
          if (departments) {
            const department = departments.get(departmentId);
            if (department) {
              assigneeData.departmentUsers = TaskModel.taskStepAndAssignee.filterUsers(
                department.users(),
                languageId,
              );
              if (assigneeData.userId <= 0) {
                assigneeData.allUsers =
                  assigneeData.departmentUsers && assigneeData.departmentUsers.length > 0;
                taskStepAssignee.set("allUsers", assigneeData.allUsers);
              }
            }
          }
        }
        const model = new TaskModel.taskStepAndAssignee(assigneeData, { parse: true });
        model.taskStep = taskStep;
        model.taskStepAssignee = taskStepAssignee;
        return model;
      },
      filterUsers: function (allUsers, languageId, kbId) {
        kbId = kbId || Messenger.reqres.request("active:kb");
        const filteredUsers = [];
        allUsers.each(function (user) {
          if (
            (user.get("languageId") == languageId || languageId <= 0) &&
            user.get("kbId") == kbId &&
            !_.find(filteredUsers, function (u) {
              return u.get("userId") == user.get("userId");
            })
          ) {
            filteredUsers.push(user);
          }
        });
        return filteredUsers;
      },
    },
  );

  TaskModel.taskUpload = Backbone.Model.extend({
    defaults: function () {
      return {
        description: "",
        kbId: Messenger.reqres.request("active:kb"),
        taskId: 0,
        uploadFileName: "",
        uploadId: "",
        uploadUrl: "",
      };
    },
  });

  TaskModel.taskUploads = Backbone.Collection.extend({
    model: TaskModel.taskUpload,
    addItem: function (data) {
      data = data || {};
      if (this.task && !this.task.isNew()) {
        data.taskId = this.task.get("taskId");
      }
      if (!data.uploadUrl) {
        data.uploadUrl = Messenger.reqres.request("get:download:url", data.uploadId, data.fileName);
      }
      const upload = new TaskModel.taskUpload(data);
      this.add(upload);
      return upload;
    },
  });

  TaskModel.topicEditRequest = Backbone.Model.extend({
    defaults: {
      comment: "",
      response: "",
    },
    url: function () {
      let kbId = this.get("kbId");
      if (!kbId) {
        kbId = Messenger.reqres.request("active:kb");
      }
      let url = Properties.rootURL + "/" + kbId + "/tasks/topicEditRequest";
      if (this.id) {
        url += "/" + this.id;
      }
      return url;
    },
  });

  Messenger.reqres.setHandler(
    "tasks:by:assignee:and:status",
    function (login, statusId) {
      const tasks = new TaskModel.tasks();
      const kbId = Messenger.reqres.request("active:kb");
      tasks.url =
        Properties.rootURL +
        "/" +
        kbId +
        "/tasks/assignee_login/" +
        encodeURIComponent(login) +
        "/status/" +
        statusId;
      tasks.fetch();
      return tasks;
    },
    this,
  );

  Messenger.reqres.setHandler(
    "tasks:by:creator:and:status",
    function (login, statusId) {
      const tasks = new TaskModel.tasks();
      const kbId = Messenger.reqres.request("active:kb");
      tasks.url =
        Properties.rootURL +
        "/" +
        kbId +
        "/tasks/creator_login/" +
        encodeURIComponent(login) +
        "/status/" +
        statusId;
      tasks.fetch();
      return tasks;
    },
    this,
  );

  Messenger.reqres.setHandler(
    "tasks:by:other:creator:and:status",
    function (login, statusId) {
      const tasks = new TaskModel.tasks();
      const kbId = Messenger.reqres.request("active:kb");
      tasks.url =
        Properties.rootURL +
        "/" +
        kbId +
        "/tasks/other_creators/" +
        encodeURIComponent(login) +
        "/status/" +
        statusId;
      tasks.fetch();
      return tasks;
    },
    this,
  );

  Messenger.reqres.setHandler("task:changeTypes", function () {
    return CHANGE_TYPE;
  });

  let taskIntervals;

  Messenger.reqres.setHandler("task:intervals", function () {
    if (!taskIntervals) {
      taskIntervals = new Backbone.Collection();
      for (const taskIntervalKey in INTERVAL) {
        const taskIntervalValue = INTERVAL[taskIntervalKey];
        taskIntervals.add(
          new Backbone.Model({
            id: taskIntervalValue,
            pluralText: $.t("task.interval" + taskIntervalValue, { count: 2 }),
            singularText: $.t("task.interval" + taskIntervalValue, { count: 1 }),
          }),
        );
      }
    }
    return taskIntervals;
  });

  Messenger.reqres.setHandler("task:intervals:withoutNone", function () {
    if (!taskIntervals) {
      taskIntervals = new Backbone.Collection();
      for (const taskIntervalValue of Object.values(INTERVAL).filter(
        (interval) => interval !== INTERVAL.NONE,
      )) {
        taskIntervals.add(
          new Backbone.Model({
            id: taskIntervalValue,
            pluralText: $.t("task.interval" + taskIntervalValue, { count: 2 }),
            singularText: $.t("task.interval" + taskIntervalValue, { count: 1 }),
          }),
        );
      }
    }
    return taskIntervals;
  });

  Messenger.reqres.setHandler("task:priorities", function () {
    return new Backbone.Collection(
      [
        {
          id: PRIORITY.LOW,
          days: PRIORITY_DAYS.LOW,
          text: $.t("task.priority.low"),
        },
        {
          id: PRIORITY.MEDIUM,
          days: PRIORITY_DAYS.MEDIUM,
          text: $.t("task.priority.medium"),
        },
        {
          id: PRIORITY.HIGH,
          days: PRIORITY_DAYS.HIGH,
          text: $.t("task.priority.high"),
        },
        {
          id: PRIORITY.CRITICAL,
          days: PRIORITY_DAYS.CRITICAL,
          text: $.t("task.priority.critical"),
        },
      ],
      { parse: true },
    );
  });

  Messenger.reqres.setHandler("task:statuses", function () {
    return new Backbone.Collection(
      [
        {
          id: STATUS.NOT_STARTED,
          text: $.t("task.status.notStarted"),
        },
        {
          id: STATUS.COMPLETED,
          text: $.t("task.status.completed"),
        },
        {
          id: STATUS.FAILED,
          text: $.t("task.status.rejected"),
        },
        {
          id: STATUS.IN_PROGRESS,
          text: $.t("task.status.inProgress"),
        },
        {
          id: STATUS.IGNORED,
          text: $.t("task.status.ignored"),
        },
        {
          id: STATUS.RESUBMITTED,
          text: $.t("task.status.resubmitted"),
        },
        {
          id: STATUS.CREATED,
          text: $.t("task.status.created"),
        },
        {
          id: STATUS.RECENTLY_COMPLETED,
          text: $.t("task.status.recentlyCompleted"),
        },
      ],
      { parse: true },
    );
  });

  Messenger.reqres.setHandler("task:status:display", function (statusId) {
    switch (statusId) {
      case STATUS.NOT_STARTED:
        return $.t("task.status.notStarted");
      case STATUS.COMPLETED:
        return $.t("task.status.completed");
      case STATUS.FAILED:
        return $.t("task.status.rejected");
      case STATUS.IN_PROGRESS:
        return $.t("task.status.inProgress");
      case STATUS.IGNORED:
        return $.t("task.status.ignored");
      case STATUS.RESUBMITTED:
        return $.t("task.status.resubmitted");
      case STATUS.CREATED:
        return $.t("task.status.created");
      case STATUS.RECENTLY_COMPLETED:
        return $.t("task.status.recentlyCompleted");
    }
  });

  Messenger.reqres.setHandler("task:templates", function () {
    const tasks = new TaskModel.tasks();
    const kbId = Messenger.reqres.request("active:kb");
    tasks.url = Properties.rootURL + "/" + kbId + "/tasks/templates";
    tasks.fetch();
    return tasks;
  });

  Messenger.reqres.setHandler("task:createAdvanced", function () {
    const user = Messenger.reqres.request("user");
    return new TaskModel.task({
      type: TaskModel.TYPE.ADVANCED,
      creatorId: user.get("userId"),
      creator: user.get("userName"),
      intentId: 0,
      intentName: "",
      status: STATUS.CREATED,
      startDate: moment.utc().startOf("day").toDate(),
      priority: TaskModel.PRIORITY.LOW,
      dueDate: moment.utc().add(TaskModel.PRIORITY_DAYS.LOW, "days").startOf("day").toDate(),
    });
  });

  Messenger.reqres.setHandler("topicEdit:statuses", function () {
    return new Backbone.Collection(
      [
        {
          id: TOPIC_EDIT_STATUS.SUBMITTED,
          text: $.t("task.topicEditStatus.submitted"),
        },
        {
          id: TOPIC_EDIT_STATUS.ACCEPTED,
          text: $.t("task.topicEditStatus.accepted"),
        },
        {
          id: TOPIC_EDIT_STATUS.REJECTED,
          text: $.t("task.topicEditStatus.rejected"),
        },
        {
          id: TOPIC_EDIT_STATUS.STARTED,
          text: $.t("task.topicEditStatus.started"),
        },
        {
          id: TOPIC_EDIT_STATUS.COMPLETE,
          text: $.t("task.topicEditStatus.complete"),
        },
      ],
      { parse: true },
    );
  });

  Messenger.reqres.setHandler("fetch:topicEditRequest", function (options) {
    const kbId = options.kbId || Messenger.reqres.request("active:kb");
    const id = options.id;
    if (!id) {
      console.error("No ID given for fetch:topicEditRequest");
      return null;
    }
    const topicEditRequest = new TaskModel.topicEditRequest({
      id: id,
      kbId: kbId,
    });
    topicEditRequest.fetch();
    return topicEditRequest;
  });

  Messenger.reqres.setHandler("fetch:task", _.noop);

  return TaskModel;
});
